// ============================================================
// Fee Installments Page
// ============================================================
// Dedicated page for managing installment schedules for a fee component.

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Button, Chip, IconButton, Tooltip, Stack, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Edit2, Trash2, Calendar, CheckCircle2, AlertCircle, Sparkles, X } from 'lucide-react';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useSchool } from '@/hooks/useSchool';
import { feeService } from '@/services/feeService';
import { academicSessionsApi } from '@/api/academicSessions.api';
import { useFeeTypes } from '@/hooks/useFeeTypes';
import { useFeeStructureItems } from '@/hooks/useFeeStructureItems';
import { useFeeInstallments, useCreateFeeInstallments, useUpdateFeeInstallment, useDeleteFeeInstallment } from '@/hooks/useFeeInstallments';
import { PATHS } from '@/router/paths';
import { tokens } from '@/theme/tokens';
import toast from 'react-hot-toast';
import { FeeSetupGuideBanner } from '@/components/fees/FeeSetupGuideBanner';
import { InstallmentPreviewDialog } from './components/InstallmentPreviewDialog';
import { InstallmentFormDialog } from './components/InstallmentFormDialog';
import { FREQUENCY_DIVISIONS } from '@/constants/feeInstallments';

export function FeeInstallmentsPage() {
  const { structureId, itemId } = useParams();
  const navigate = useNavigate();
  const { schoolId } = useSchool();

  usePageMeta('Installment Schedule', [
    { label: 'Fees', path: PATHS.FEES },
    { label: 'Structure', path: PATHS.FEES_STRUCTURE },
    { label: 'Components', path: `/fees/structure/${structureId}/components` },
    { label: 'Installments' },
  ]);

  // Context & Lookup states
  const [feeStructure, setFeeStructure] = useState(null);
  const [sessions, setSessions] = useState([]);
  const { data: feeTypes = [] } = useFeeTypes();
  const { data: items = [], isLoading: _itemsLoading } = useFeeStructureItems(structureId);
  const { data: installments = [], isLoading: installmentsLoading } = useFeeInstallments(itemId);

  const { mutate: createInstallments, isPending: isCreating } = useCreateFeeInstallments();
  const { mutate: updateInstallment, isPending: isUpdating } = useUpdateFeeInstallment();
  const { mutate: deleteInstallment, isPending: _isDeleting } = useDeleteFeeInstallment();

  // Target component item & fee type info
  const targetItem = useMemo(() => items.find((i) => i.id === itemId), [items, itemId]);
  const feeTypeInfo = useMemo(() => {
    if (!targetItem) return null;
    return targetItem.fee_type || feeTypes.find((ft) => ft.id === targetItem.fee_type_id);
  }, [targetItem, feeTypes]);

  // Fetch Fee Structure metadata & Academic Sessions
  useEffect(() => {
    if (schoolId && structureId) {
      feeService
        .getFeeStructureById(schoolId, structureId)
        .then((res) => setFeeStructure(res?.data || res))
        .catch(console.error);

      academicSessionsApi
        .getAllBySchool(schoolId)
        .then((res) => setSessions(Array.isArray(res) ? res : res?.data || []))
        .catch(console.error);
    }
  }, [schoolId, structureId]);

  // Determine current or linked academic session
  const targetSession = useMemo(() => {
    if (feeStructure?.academic_year_id && sessions.length > 0) {
      const found = sessions.find((s) => s.id === feeStructure.academic_year_id);
      if (found) return found;
    }
    return sessions.find((s) => s.is_current) || sessions[0] || null;
  }, [feeStructure, sessions]);

  // Extract Session Start Year & Month (0-indexed)
  const sessionStartDateInfo = useMemo(() => {
    if (targetSession?.start_date) {
      const parts = targetSession.start_date.split('T')[0].split('-');
      if (parts.length >= 2) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) - 1; // 0-indexed: 0 for Jan, 3 for Apr
        if (!isNaN(y) && !isNaN(m) && m >= 0 && m <= 11) {
          return { startYear: y, startMonth: m };
        }
      }
    }
    const today = new Date();
    return { startYear: today.getFullYear(), startMonth: 3 }; // default April of current year
  }, [targetSession]);

  // Total Component Amount vs Total Scheduled Amount
  const totalComponentAmount = Number(targetItem?.amount) || 0;
  const totalScheduledAmount = useMemo(() => {
    return installments.reduce((sum, inst) => sum + (Number(inst.amount) || 0), 0);
  }, [installments]);

  const amountDifference = Number((totalComponentAmount - totalScheduledAmount).toFixed(2));
  const isMatch = Math.abs(amountDifference) < 0.01 && installments.length > 0;

  // Single Add / Edit Modal State
  const [openForm, setOpenForm] = useState(false);
  const [editingInst, setEditingInst] = useState(null);
  const [formData, setFormData] = useState({
    installment_name: '',
    amount: '',
    due_date: new Date().toISOString().split('T')[0],
    grace_days: 0,
    late_fee_applicable: true,
    is_active: true,
  });

  // Interactive Multi-Row Preview & Editor Dialog State
  const [openPreview, setOpenPreview] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);

  // Direct Auto-Generate without preview (starts from current session start date)
  const handleDirectAutoGenerate = () => {
    if (totalComponentAmount <= 0) {
      toast.error('Component fee amount is 0. Please set a valid component amount first.');
      return;
    }

    const freqKey = feeTypeInfo?.frequency || targetItem?.installment_type || 'MONTHLY';
    const division = FREQUENCY_DIVISIONS[freqKey] || FREQUENCY_DIVISIONS.MONTHLY;
    const count = division.count;

    const baseAmount = Math.floor((totalComponentAmount / count) * 100) / 100;
    const totalBase = baseAmount * count;
    const remainder = Number((totalComponentAmount - totalBase).toFixed(2));

    const { startYear, startMonth } = sessionStartDateInfo;
    const dueDay = targetItem?.due_day || 10;
    const monthStep = freqKey === 'QUARTERLY' ? 3 : freqKey === 'HALF_YEARLY' ? 6 : freqKey === 'PER_TERM' ? 4 : 1;
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const payloadArray = Array.from({ length: count }, (_, idx) => {
      const monthOffset = idx * monthStep;
      const totalMonths = startMonth + monthOffset;
      const targetYear = startYear + Math.floor(totalMonths / 12);
      const targetMonthIdx = totalMonths % 12;
      const daysInMonth = new Date(targetYear, targetMonthIdx + 1, 0).getDate();
      const targetDay = Math.min(dueDay, daysInMonth);
      const dateStr = `${targetYear}-${String(targetMonthIdx + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;

      const isLast = idx === count - 1;
      const instAmount = isLast ? Number((baseAmount + remainder).toFixed(2)) : baseAmount;

      const defaultName =
        count === 1
          ? (freqKey === 'YEARLY' || freqKey === 'ANNUALLY' ? 'Annual Fee' : 'One Time Fee')
          : freqKey === 'MONTHLY'
            ? monthNames[targetMonthIdx]
            : `${division.itemPrefix} ${idx + 1} Fee`;

      return {
        installment_name: defaultName,
        installment_no: idx + 1,
        amount: instAmount,
        due_date: dateStr,
        grace_days: 0,
        late_fee_applicable: true,
        is_active: true,
      };
    });

    createInstallments(
      { feeStructureItemId: itemId, payloadArray },
      {
        onSuccess: () => {
          toast.success(`Successfully auto-generated ${count} equal installments!`);
        },
        onError: (err) => toast.error(err.message || 'Failed to auto-generate installments'),
      }
    );
  };

  // Generate Frequency Draft Rows for Preview Modal (starts from current session start date)
  const handleOpenAutoPreview = () => {
    if (totalComponentAmount <= 0) {
      toast.error('Component fee amount is 0. Please set a valid component amount first.');
      return;
    }

    const freqKey = feeTypeInfo?.frequency || targetItem?.installment_type || 'MONTHLY';
    const division = FREQUENCY_DIVISIONS[freqKey] || FREQUENCY_DIVISIONS.MONTHLY;
    const count = division.count;

    const baseAmount = Math.floor((totalComponentAmount / count) * 100) / 100;
    const totalBase = baseAmount * count;
    const remainder = Number((totalComponentAmount - totalBase).toFixed(2));

    const { startYear, startMonth } = sessionStartDateInfo;
    const dueDay = targetItem?.due_day || 10;
    const monthStep = freqKey === 'QUARTERLY' ? 3 : freqKey === 'HALF_YEARLY' ? 6 : freqKey === 'PER_TERM' ? 4 : 1;
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const generated = Array.from({ length: count }, (_, idx) => {
      const monthOffset = idx * monthStep;
      const totalMonths = startMonth + monthOffset;
      const targetYear = startYear + Math.floor(totalMonths / 12);
      const targetMonthIdx = totalMonths % 12;
      const daysInMonth = new Date(targetYear, targetMonthIdx + 1, 0).getDate();
      const targetDay = Math.min(dueDay, daysInMonth);
      const dateStr = `${targetYear}-${String(targetMonthIdx + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;

      const isLast = idx === count - 1;
      const instAmount = isLast ? Number((baseAmount + remainder).toFixed(2)) : baseAmount;

      const defaultName =
        count === 1
          ? (freqKey === 'YEARLY' || freqKey === 'ANNUALLY' ? 'Annual Fee' : 'One Time Fee')
          : freqKey === 'MONTHLY'
            ? monthNames[targetMonthIdx]
            : `${division.itemPrefix} ${idx + 1} Fee`;

      return {
        tempId: `draft-${idx + 1}`,
        installment_name: defaultName,
        installment_no: idx + 1,
        amount: String(instAmount),
        due_date: dateStr,
        grace_days: 0,
        late_fee_applicable: true,
        is_active: true,
      };
    });

    setPreviewRows(generated);
    setOpenPreview(true);
  };

  // Open Preview Modal with Current Installments for Editing
  const _handleOpenExistingPreview = () => {
    if (installments.length === 0) {
      handleOpenAutoPreview();
      return;
    }
    const currentRows = installments.map((i, idx) => ({
      tempId: i.id || `inst-${idx + 1}`,
      installment_name: i.installment_name || `Installment ${idx + 1}`,
      installment_no: idx + 1,
      amount: String(i.amount || 0),
      due_date: i.due_date ? i.due_date.split('T')[0] : new Date().toISOString().split('T')[0],
      grace_days: i.grace_days || 0,
      late_fee_applicable: !!i.late_fee_applicable,
      is_active: i.is_active !== false,
    }));
    setPreviewRows(currentRows);
    setOpenPreview(true);
  };

  // Update a field inside the preview table
  const handlePreviewRowChange = (index, field, value) => {
    setPreviewRows((prev) => {
      const updated = [...prev];

      // Enforce max 30 grace days
      if (field === 'grace_days') {
        let valNum = parseInt(value, 10);
        if (isNaN(valNum) || valNum < 0) valNum = 0;
        if (valNum > 30) {
          toast.error('Grace period cannot exceed 30 days');
          valNum = 30;
        }
        value = String(valNum);
      }

      updated[index] = { ...updated[index], [field]: value };

      // Auto-fill subsequent months if the user edits installment_name of the first row (index === 0)
      if (index === 0 && field === 'installment_name' && value && updated.length > 1) {
        const monthsFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        let matchedIndex = -1;
        let isFull = true;

        for (let i = 0; i < 12; i++) {
          if (new RegExp(`\\b${monthsFull[i]}\\b`, 'i').test(value)) {
            matchedIndex = i;
            isFull = true;
            break;
          }
        }
        if (matchedIndex === -1) {
          for (let i = 0; i < 12; i++) {
            if (new RegExp(`\\b${monthsShort[i]}\\b`, 'i').test(value)) {
              matchedIndex = i;
              isFull = false;
              break;
            }
          }
        }

        if (matchedIndex !== -1) {
          const list = isFull ? monthsFull : monthsShort;
          const matchWord = isFull ? monthsFull[matchedIndex] : monthsShort[matchedIndex];
          const replaceRegex = new RegExp(`\\b${matchWord}\\b`, 'i');
          const step = updated.length === 4 ? 3 : updated.length === 2 ? 6 : updated.length === 3 ? 4 : 1;

          const yearMatch = value.match(/\b(20\d\d)\b/);
          const startYear = yearMatch ? parseInt(yearMatch[1], 10) : null;

          for (let rowIdx = 1; rowIdx < updated.length; rowIdx++) {
            const totalMonths = matchedIndex + rowIdx * step;
            const targetMonthIdx = totalMonths % 12;
            const targetMonthName = list[targetMonthIdx];
            let newName = value.replace(replaceRegex, targetMonthName);

            if (startYear !== null) {
              const yearOffset = Math.floor(totalMonths / 12);
              if (yearOffset > 0) {
                const targetYear = startYear + yearOffset;
                newName = newName.replace(String(startYear), String(targetYear));
              }
            }

            updated[rowIdx] = {
              ...updated[rowIdx],
              installment_name: newName,
            };
          }
        }
      }

      // Auto-fill subsequent dates & months when the user edits due_date of any row
      if (field === 'due_date' && value && updated.length > index + 1) {
        const parts = value.split('-');
        if (parts.length === 3) {
          const startYear = parseInt(parts[0], 10);
          const startMonthIdx = parseInt(parts[1], 10) - 1;
          const dayNum = parseInt(parts[2], 10);
          const monthsFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
          const step = updated.length === 4 ? 3 : updated.length === 2 ? 6 : updated.length === 3 ? 4 : 1;

          updated[index] = {
            ...updated[index],
            installment_name: monthsFull[startMonthIdx],
          };

          for (let rowIdx = index + 1; rowIdx < updated.length; rowIdx++) {
            const rowOffset = rowIdx - index;
            const totalMonths = startMonthIdx + rowOffset * step;
            const targetYear = startYear + Math.floor(totalMonths / 12);
            const targetMonthIdx = totalMonths % 12;
            const daysInMonth = new Date(targetYear, targetMonthIdx + 1, 0).getDate();
            const targetDay = Math.min(dayNum, daysInMonth);
            const targetDateStr = `${targetYear}-${String(targetMonthIdx + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;

            updated[rowIdx] = {
              ...updated[rowIdx],
              due_date: targetDateStr,
              installment_name: monthsFull[targetMonthIdx],
            };
          }
        }
      }

      return updated;
    });
  };

  // Add a new row to preview table
  const handleAddPreviewRow = () => {
    const nextIdx = previewRows.length;
    const freqKey = feeTypeInfo?.frequency || targetItem?.installment_type || 'MONTHLY';
    const monthStep = freqKey === 'QUARTERLY' ? 3 : freqKey === 'HALF_YEARLY' ? 6 : freqKey === 'PER_TERM' ? 4 : 1;
    const totalMonths = sessionStartDateInfo.startMonth + nextIdx * monthStep;
    const targetYear = sessionStartDateInfo.startYear + Math.floor(totalMonths / 12);
    const targetMonthIdx = totalMonths % 12;
    const dueDay = targetItem?.due_day || 10;
    const daysInMonth = new Date(targetYear, targetMonthIdx + 1, 0).getDate();
    const targetDay = Math.min(dueDay, daysInMonth);
    const dateStr = `${targetYear}-${String(targetMonthIdx + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    setPreviewRows((prev) => [
      ...prev,
      {
        tempId: `draft-${Date.now()}`,
        installment_name: freqKey === 'MONTHLY' ? monthNames[targetMonthIdx] : `Installment ${prev.length + 1}`,
        installment_no: prev.length + 1,
        amount: '',
        due_date: dateStr,
        grace_days: 0,
        late_fee_applicable: true,
        is_active: true,
      },
    ]);
  };

  // Remove a row from preview table
  const handleRemovePreviewRow = (index) => {
    setPreviewRows((prev) => prev.filter((_, i) => i !== index));
  };

  // Calculate live total sum inside preview modal
  const previewTotalSum = useMemo(() => {
    return previewRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  }, [previewRows]);

  const previewDiff = Number((totalComponentAmount - previewTotalSum).toFixed(2));
  const isPreviewMatch = Math.abs(previewDiff) < 0.01 && previewRows.length > 0;

  // Save all previewed installments to backend
  const handleSavePreviewSchedule = () => {
    if (previewRows.length === 0) {
      toast.error('At least one installment row is required.');
      return;
    }

    if (!isPreviewMatch) {
      toast.error(
        `Total installments (₹${previewTotalSum.toLocaleString()}) must match the component amount of ₹${totalComponentAmount.toLocaleString()}`
      );
      return;
    }

    const payloadArray = previewRows.map((r, idx) => ({
      installment_name: r.installment_name.trim() || `Installment ${idx + 1}`,
      installment_no: idx + 1,
      amount: Number(r.amount),
      due_date: r.due_date,
      grace_days: Number(r.grace_days || 0),
      late_fee_applicable: !!r.late_fee_applicable,
      is_active: r.is_active !== false,
    }));

    createInstallments(
      { feeStructureItemId: itemId, payloadArray },
      {
        onSuccess: () => {
          toast.success('Installment schedule saved successfully!');
          setOpenPreview(false);
        },
        onError: (err) => toast.error(err.message || 'Failed to save installment schedule'),
      }
    );
  };

  // Single Item Open Add / Edit Handlers
  const _handleOpenAdd = () => {
    setEditingInst(null);
    const suggestedAmount = amountDifference > 0 ? String(amountDifference) : '';
    const nextIdx = installments.length;
    const freqKey = feeTypeInfo?.frequency || targetItem?.installment_type || 'MONTHLY';
    const monthStep = freqKey === 'QUARTERLY' ? 3 : freqKey === 'HALF_YEARLY' ? 6 : freqKey === 'PER_TERM' ? 4 : 1;
    const totalMonths = sessionStartDateInfo.startMonth + nextIdx * monthStep;
    const targetYear = sessionStartDateInfo.startYear + Math.floor(totalMonths / 12);
    const targetMonthIdx = totalMonths % 12;
    const dueDay = targetItem?.due_day || 10;
    const daysInMonth = new Date(targetYear, targetMonthIdx + 1, 0).getDate();
    const targetDay = Math.min(dueDay, daysInMonth);
    const defaultDueDate = `${targetYear}-${String(targetMonthIdx + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    setFormData({
      installment_name: freqKey === 'MONTHLY' ? monthNames[targetMonthIdx] : `Installment ${nextIdx + 1}`,
      amount: suggestedAmount,
      due_date: defaultDueDate,
      grace_days: 0,
      late_fee_applicable: true,
      is_active: true,
    });
    setOpenForm(true);
  };

  const handleOpenEdit = (inst) => {
    setEditingInst(inst);
    setFormData({
      installment_name: inst.installment_name || '',
      amount: inst.amount != null ? String(inst.amount) : '',
      due_date: inst.due_date ? inst.due_date.split('T')[0] : '',
      grace_days: inst.grace_days || 0,
      late_fee_applicable: !!inst.late_fee_applicable,
      is_active: inst.is_active !== false,
    });
    setOpenForm(true);
  };

  // Single Item Submit Handler
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.installment_name.trim()) {
      toast.error('Installment Name is required');
      return;
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      toast.error('Valid amount is required');
      return;
    }
    if (!formData.due_date) {
      toast.error('Due date is required');
      return;
    }

    const graceNum = Number(formData.grace_days);
    if (isNaN(graceNum) || graceNum < 0 || graceNum > 30) {
      toast.error('Grace period cannot exceed 30 days');
      return;
    }

    if (editingInst) {
      updateInstallment(
        {
          installmentId: editingInst.id,
          feeStructureItemId: itemId,
          payload: {
            ...formData,
            amount: Number(formData.amount),
            grace_days: Number(formData.grace_days),
          },
        },
        {
          onSuccess: () => {
            toast.success('Installment updated successfully');
            setOpenForm(false);
          },
          onError: (err) => toast.error(err.message || 'Failed to update installment'),
        }
      );
    } else {
      const newInst = {
        installment_name: formData.installment_name,
        installment_no: installments.length + 1,
        amount: Number(formData.amount),
        due_date: formData.due_date,
        grace_days: Number(formData.grace_days),
        late_fee_applicable: formData.late_fee_applicable,
        is_active: formData.is_active,
      };

      const existingClean = installments.map((i) => ({
        installment_name: i.installment_name,
        installment_no: i.installment_no,
        amount: Number(i.amount),
        due_date: i.due_date ? i.due_date.split('T')[0] : new Date().toISOString().split('T')[0],
        grace_days: Number(i.grace_days || 0),
        late_fee_applicable: !!i.late_fee_applicable,
        is_active: i.is_active !== false,
      }));

      const payloadArray = [...existingClean, newInst];

      createInstallments(
        { feeStructureItemId: itemId, payloadArray },
        {
          onSuccess: () => {
            toast.success('Installment created successfully');
            setOpenForm(false);
          },
          onError: (err) => toast.error(err.message || 'Failed to create installment'),
        }
      );
    }
  };

  const [deleteInst, setDeleteInst] = useState({ open: false, inst: null, loading: false });

  // Delete confirmation handler
  const handleConfirmDeleteInstallment = () => {
    if (!deleteInst.inst?.id) return;
    setDeleteInst((prev) => ({ ...prev, loading: true }));
    deleteInstallment(
      { installmentId: deleteInst.inst.id, feeStructureItemId: itemId },
      {
        onSuccess: () => {
          toast.success('Installment removed successfully');
          setDeleteInst({ open: false, inst: null, loading: false });
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to delete installment');
          setDeleteInst((prev) => ({ ...prev, loading: false }));
        },
      }
    );
  };

  // Main DataGrid Columns Definition
  const columns = [
    {
      field: 'installment_no',
      headerName: '#',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Chip
            label={`#${params.value || 1}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontWeight: 700, fontSize: '0.75rem' }}
          />
        </Box>
      ),
    },
    {
      field: 'installment_name',
      headerName: 'Installment Name',
      flex: 2,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 140,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', width: '100%' }}>
          <Typography variant="body2" fontWeight={700} color="primary.main">
            ₹{(Number(params.value) || 0).toLocaleString()}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {params.value ? params.value.split('T')[0] : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'grace_days',
      headerName: 'Grace Days',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {params.value != null ? `${params.value} days` : '0 days'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'late_fee_applicable',
      headerName: 'Late Fee',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Chip
            label={params.value ? 'Yes' : 'No'}
            size="small"
            color={params.value ? 'warning' : 'default'}
            variant="outlined"
          />
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created On',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="caption" color="text.secondary">
            {params.value ? params.value.split('T')[0] : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Chip
            label={params.value !== false ? 'Active' : 'Inactive'}
            size="small"
            color={params.value !== false ? 'success' : 'error'}
            sx={{ fontWeight: 500 }}
          />
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Tooltip title="Edit Installment">
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)}>
              <Edit2 size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Installment">
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteInst({ open: true, inst: params.row, loading: false }); }}>
              <Trash2 size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const freqKey = feeTypeInfo?.frequency || targetItem?.installment_type || 'MONTHLY';
  const freqLabel = FREQUENCY_DIVISIONS[freqKey]?.label || freqKey;

  return (
    <Box sx={{ pb: 6, width: '100%' }}>
      <PageHeader
        title="Manage Installment Schedule"
        subtitle={`Define payment dates & equal installments for ${feeTypeInfo?.name || 'this component'}.`}
        onBack={() => navigate(`/fees/structure/${structureId}/components`)}
      />

      {/* Interactive Guided Flow Banner */}
      <FeeSetupGuideBanner currentStep={3} structureId={structureId} itemId={itemId} />

      {/* Summary Header Card */}
      <WidgetContainer sx={{ mt: 3, p: 3, width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {feeTypeInfo?.name || 'Fee Component'}
              </Typography>
              {feeTypeInfo?.code && (
                <Chip
                  label={feeTypeInfo.code}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 700, borderColor: tokens.colors.primary.main, color: tokens.colors.primary.main }}
                />
              )}
              <Chip label={freqLabel} size="small" color="info" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Structure Plan: <strong>{feeStructure?.name || 'Academic Fee Plan'}</strong>
              {targetSession?.session_name && (
                <Chip
                  label={`Session: ${targetSession.session_name.replace(/^Academic Session\s*/i, '')}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ ml: 1, fontWeight: 600, fontSize: '0.725rem', height: 22 }}
                />
              )}
            </Typography>
          </Box>

          <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Component Total Fee
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                ₹{totalComponentAmount.toLocaleString()}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Scheduled Installments Total
              </Typography>
              <Typography variant="h6" fontWeight={700} color={isMatch ? 'success.main' : 'warning.main'}>
                ₹{totalScheduledAmount.toLocaleString()}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              {installments.length === 0 ? (
                <Chip
                  icon={<AlertCircle size={14} />}
                  label="No Schedule Configured"
                  color="default"
                  sx={{ fontWeight: 600 }}
                />
              ) : isMatch ? (
                <Chip
                  icon={<CheckCircle2 size={14} />}
                  label="100% Scheduled"
                  color="success"
                  sx={{ fontWeight: 700 }}
                />
              ) : (
                <Chip
                  icon={<AlertCircle size={14} />}
                  label={amountDifference > 0 ? `Short by ₹${amountDifference.toLocaleString()}` : `Excess by ₹${Math.abs(amountDifference).toLocaleString()}`}
                  color={amountDifference > 0 ? 'warning' : 'error'}
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Box>
          </Stack>
        </Box>
      </WidgetContainer>

      {installments.length > 0 && !isMatch && (
        <Alert
          severity={amountDifference > 0 ? 'warning' : 'error'}
          sx={{ mt: 2, borderRadius: 1.5, alignItems: 'center' }}
        >
          <strong>Schedule Amount Mismatch:</strong> Your scheduled installments total <strong>₹{totalScheduledAmount.toLocaleString()}</strong>, which is <strong>₹{Math.abs(amountDifference).toLocaleString()} {amountDifference > 0 ? 'less than' : 'greater than'}</strong> the required Component Fee of <strong>₹{totalComponentAmount.toLocaleString()}</strong>. Please edit the installments below so they sum to exactly ₹{totalComponentAmount.toLocaleString()}.
        </Alert>
      )}

      {/* Main DataGrid Table Widget */}
      <WidgetContainer sx={{ mt: 3, width: '100%' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${tokens.colors.border}` }}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            Installment Breakdown ({installments.length})
          </Typography>

          {installments.length > 0 && (
            <Stack direction="row" spacing={1.5}>
              <Tooltip title="Schedule is already configured. Use the edit icon in the table below to modify individual installments.">
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Sparkles size={16} />}
                    disabled
                  >
                    Auto-Divide ({FREQUENCY_DIVISIONS[freqKey]?.count || 12} Installments)
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Schedule is already configured. Use the edit icon in the table below to modify individual installments.">
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<Edit2 size={16} />}
                    disabled
                  >
                    Preview & Customize Schedule
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Box>

        {installments.length === 0 && !installmentsLoading ? (
          <Box sx={{ p: 5, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Calendar size={40} color={tokens.colors.text.secondary} style={{ marginBottom: 12 }} />
            <Typography variant="subtitle1" fontWeight={600} color="text.primary" gutterBottom>
              No Installment Schedule Configured
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mb: 3 }}>
              Select one of the options below to set up the <strong>{FREQUENCY_DIVISIONS[freqKey]?.count || 12} {freqKey.toLowerCase()} installment(s)</strong> matching ₹{totalComponentAmount.toLocaleString()}.
            </Typography>
            <Stack direction="row" spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<Sparkles size={16} />}
                onClick={handleDirectAutoGenerate}
                disabled={isCreating}
              >
                Auto-Divide Directly ({FREQUENCY_DIVISIONS[freqKey]?.count || 12} Installments)
              </Button>
              <Button
                variant="contained"
                startIcon={<Edit2 size={16} />}
                onClick={handleOpenAutoPreview}
              >
                Preview & Customize Schedule
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <DataGrid
              autoHeight
              rows={installments}
              columns={columns}
              loading={installmentsLoading}
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </Box>
        )}
      </WidgetContainer>

      {/* Interactive Multi-Row Preview & Editor Dialog */}
      <InstallmentPreviewDialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        previewRows={previewRows}
        setPreviewRows={setPreviewRows}
        isPreviewMatch={isPreviewMatch}
        previewDiff={previewDiff}
        previewTotalSum={previewTotalSum}
        totalComponentAmount={totalComponentAmount}
        freqKey={freqKey}
        isCreating={isCreating}
        handleSavePreview={handleSavePreviewSchedule}
        handlePreviewRowChange={handlePreviewRowChange}
        handleRemovePreviewRow={handleRemovePreviewRow}
        handleAddPreviewRow={handleAddPreviewRow}
      />

      {/* Single Add / Edit Installment Dialog */}
      <InstallmentFormDialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        editingInst={editingInst}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        isCreating={isCreating}
        isUpdating={isUpdating}
      />

      {/* Compact Delete Installment Dialog */}
      <Dialog
        open={deleteInst.open}
        onClose={() => setDeleteInst({ open: false, inst: null, loading: false })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            p: 0.5,
            boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, pt: 1.5, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            Remove Installment
          </Typography>
          <IconButton size="small" onClick={() => setDeleteInst({ open: false, inst: null, loading: false })}>
            <X size={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 1, px: 2 }}>
          {deleteInst.inst && (
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to remove <strong>{deleteInst.inst.installment_name}</strong> (₹{(Number(deleteInst.inst.amount) || 0).toLocaleString()})?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setDeleteInst({ open: false, inst: null, loading: false })}
            disabled={deleteInst.loading}
            sx={{ borderRadius: 1.5, px: 2, py: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteInstallment}
            disabled={deleteInst.loading}
            sx={{ borderRadius: 1.5, fontWeight: 600, px: 2.5, py: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            {deleteInst.loading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
